import { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, ChevronLeft, ChevronRight, Check, MapPin, Users, TrendingUp, Award, Sparkles, HeartHandshake } from 'lucide-react';

const franchiseBenefits = [
  {
    id: 1,
    title: "Pioneer in Nail & Beauty Industry",
    content: "BLR Nail Club is Bangalore's premier nail and beauty destination, known for exceptional quality in nail extensions, lash services, and premium beauty treatments. Join a brand that's setting new standards in the industry."
  },
  {
    id: 2,
    title: "Expert Team & Training",
    content: "Our skilled artisans are trained in the latest techniques from international standards. As a franchise partner, you'll get access to our comprehensive training programs covering nail art, extensions, lash applications, and customer service excellence."
  },
  {
    id: 3,
    title: "Complete Beauty Solutions",
    content: "Premium Nail Extensions & Nail Art, Classic & Volume Lash Extensions, Hair Services, Skin Treatments, Bridal Packages, and more. One destination for all beauty needs."
  },
  {
    id: 4,
    title: "Loyal Customer Base",
    content: "With 4.8★ Google rating and 400+ happy customers, we've built a loyal community. Our membership programs ensure recurring revenue and long-term customer relationships."
  },
  {
    id: 5,
    title: "Premium Brand Positioning",
    content: "Our luxurious salon ambiance, premium products, and exceptional service quality position us as a premium brand commanding better margins and customer loyalty."
  },
  {
    id: 6,
    title: "Proven Success Model",
    content: "With multiple successful locations in Bangalore (RR Nagar, Basaveshwarnagar, Varthur), our business model is tested and proven for profitability."
  }
];

const whyChooseUs = [
  { number: "01", title: "Proven Business Model", icon: TrendingUp },
  { number: "02", title: "Complete Training & Support", icon: Users },
  { number: "03", title: "Premium Brand Value", icon: Award },
  { number: "04", title: "High Profit Margins", icon: Sparkles },
  { number: "05", title: "Growing Industry", icon: TrendingUp },
  { number: "06", title: "End-to-End Assistance", icon: HeartHandshake }
];

const supportServices = [
  {
    id: 1,
    title: "Location Selection & Setup",
    content: "We help you identify the ideal location and guide you through the complete salon setup process with our proven layouts and designs."
  },
  {
    id: 2,
    title: "Marketing & Branding",
    content: "Leverage our established brand presence with ready-to-use marketing materials, social media support, and local market strategies."
  },
  {
    id: 3,
    title: "Training Academy",
    content: "Comprehensive training for you and your team covering all services, customer handling, and operational procedures."
  },
  {
    id: 4,
    title: "Operational Excellence",
    content: "Ongoing support for day-to-day operations, quality standards, and best practices to ensure consistent service delivery."
  },
  {
    id: 5,
    title: "Product & Equipment",
    content: "Access to premium products and equipment at competitive rates through our established vendor partnerships."
  },
  {
    id: 6,
    title: "Technology & Systems",
    content: "Modern booking systems, inventory management, and business analytics tools to run your salon efficiently."
  }
];

const FranchisePage = () => {
  const [expandedBenefit, setExpandedBenefit] = useState(1);
  const [expandedSupport, setExpandedSupport] = useState(1);
  const [currentSlide, setCurrentSlide] = useState(0);
  const videoRef = useRef(null);
  const formRef = useRef(null);

  // Video zoom effect
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      let scale = 1;
      const zoomInterval = setInterval(() => {
        scale = scale >= 1.15 ? 1 : scale + 0.0005;
        video.style.transform = `scale(${scale})`;
      }, 50);
      return () => clearInterval(zoomInterval);
    }
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Franchise Opportunity | BLR Nail Club - Start Your Beauty Business in Bangalore</title>
        <meta name="description" content="Partner with BLR Nail Club - Bangalore's premium nail & beauty brand. Low investment, high returns. Complete training & support. Start your franchise journey today!" />
        <meta name="keywords" content="nail salon franchise Bangalore, beauty salon franchise India, BLR Nail Club franchise, nail business opportunity, salon franchise cost" />
        <link rel="canonical" href="https://www.blrnailclub.in/franchise" />
      </Helmet>

      <main className="bg-black" data-testid="franchise-page">
        {/* Hero Section with Video Background */}
        <section className="relative h-screen overflow-hidden">
          {/* Background Video with Zoom Effect */}
          <div className="absolute inset-0 overflow-hidden">
            <video
              ref={videoRef}
              className="w-full h-full object-cover transition-transform duration-100"
              autoPlay
              loop
              muted
              playsInline
              style={{ transform: 'scale(1)' }}
            >
              <source src="https://customer-assets.emergentagent.com/job_nail-art-lounge/artifacts/k106a8cq_Video-884.mp4" type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-black/60"></div>
          </div>

          {/* Hero Content */}
          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
            <p className="text-[#B8860B] text-sm tracking-[0.3em] uppercase mb-6 font-light">Franchise Opportunity</p>
            <h1 className="text-4xl md:text-5xl lg:text-7xl font-light text-white max-w-5xl leading-tight mb-8">
              Be Part of Bangalore's<br />
              <span className="italic">Premier Beauty Brand</span>
            </h1>
            <button
              onClick={scrollToForm}
              className="bg-[#B8860B] text-black px-10 py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-[#D4AF37] transition-colors"
            >
              Start Your Franchise Journey
            </button>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50">
            <span className="text-xs tracking-widest uppercase">Scroll</span>
            <div className="w-px h-12 bg-gradient-to-b from-white/50 to-transparent"></div>
          </div>
        </section>

        {/* What Makes Us Different */}
        <section className="py-20 md:py-32 bg-[#0a0a0a]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white text-center mb-16">
              What makes a <span className="text-[#B8860B]">BLR Nail Club</span> franchise different?
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
              {/* Image */}
              <div className="relative h-[400px] lg:h-[600px] rounded-lg overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1560066984-138dadb4c035?w=800&h=1200&fit=crop"
                  alt="Premium Nail Salon Interior"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              </div>

              {/* Expandable Benefits */}
              <div className="space-y-4">
                {franchiseBenefits.map((benefit) => (
                  <div
                    key={benefit.id}
                    className={`border-b border-white/10 transition-all duration-300 ${
                      expandedBenefit === benefit.id ? 'pb-6' : 'pb-4'
                    }`}
                  >
                    <button
                      onClick={() => setExpandedBenefit(expandedBenefit === benefit.id ? null : benefit.id)}
                      className="w-full flex items-center justify-between py-4 text-left group"
                    >
                      <h3 className={`text-lg md:text-xl font-light transition-colors ${
                        expandedBenefit === benefit.id ? 'text-[#B8860B]' : 'text-white group-hover:text-[#B8860B]'
                      }`}>
                        {benefit.title}
                      </h3>
                      <span className={`text-2xl transition-transform ${
                        expandedBenefit === benefit.id ? 'rotate-45 text-[#B8860B]' : 'text-white/50'
                      }`}>
                        +
                      </span>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${
                      expandedBenefit === benefit.id ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
                    }`}>
                      <p className="text-white/60 font-light leading-relaxed pr-8">
                        {benefit.content}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 md:py-32 bg-black">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white text-center mb-16">
              Why choose <span className="text-[#B8860B]">BLR Nail Club</span>
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {whyChooseUs.map((item, index) => (
                <div
                  key={index}
                  className="group text-center p-6 border border-white/10 hover:border-[#B8860B]/50 transition-all duration-300 hover:-translate-y-1"
                >
                  <span className="text-[#B8860B] text-3xl font-light mb-4 block">{item.number}</span>
                  <item.icon className="w-8 h-8 mx-auto mb-4 text-white/40 group-hover:text-[#B8860B] transition-colors" />
                  <p className="text-white text-sm font-light">{item.title}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Partner Requirements */}
        <section className="py-20 md:py-32 bg-[#0a0a0a]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white text-center mb-16">
              Franchise Partner Requirements
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                {[
                  "Passion for beauty and wellness industry",
                  "Strong business acumen and leadership skills",
                  "Entrepreneurial spirit with commitment to excellence",
                  "Minimum investment capital of INR 25-50 Lakhs",
                  "Space requirement: 800-1500 sq ft in prime location",
                  "Willingness to follow brand standards and operations"
                ].map((requirement, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-6 h-6 rounded-full bg-[#B8860B]/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <Check className="w-4 h-4 text-[#B8860B]" />
                    </div>
                    <p className="text-white/80 font-light text-lg">{requirement}</p>
                  </div>
                ))}
              </div>

              <div className="relative h-[400px] lg:h-[500px] rounded-lg overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=800&h=1000&fit=crop"
                  alt="Franchise Partner"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Support Section with Video Background */}
        <section className="relative py-20 md:py-32 overflow-hidden">
          {/* Background Video */}
          <div className="absolute inset-0">
            <video
              className="w-full h-full object-cover"
              autoPlay
              loop
              muted
              playsInline
            >
              <source src="https://customer-assets.emergentagent.com/job_nail-art-lounge/artifacts/k106a8cq_Video-884.mp4" type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-black/80"></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-col lg:flex-row items-start justify-between gap-8 mb-12">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white">
                How do we support your<br />
                <span className="text-[#B8860B]">franchise journey?</span>
              </h2>
              <button
                onClick={scrollToForm}
                className="bg-[#B8860B] text-black px-8 py-3 text-sm tracking-[0.15em] uppercase font-medium hover:bg-[#D4AF37] transition-colors"
              >
                Join Us
              </button>
            </div>

            {/* Support Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {supportServices.map((service) => (
                <div
                  key={service.id}
                  className="bg-black/50 backdrop-blur-sm border border-white/10 p-6 hover:border-[#B8860B]/30 transition-all"
                >
                  <button
                    onClick={() => setExpandedSupport(expandedSupport === service.id ? null : service.id)}
                    className="w-full flex items-center justify-between text-left mb-4"
                  >
                    <h3 className={`text-lg font-light transition-colors ${
                      expandedSupport === service.id ? 'text-[#B8860B]' : 'text-white'
                    }`}>
                      {service.title}
                    </h3>
                    <span className={`text-xl transition-transform ${
                      expandedSupport === service.id ? 'rotate-45 text-[#B8860B]' : 'text-white/50'
                    }`}>
                      +
                    </span>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${
                    expandedSupport === service.id ? 'max-h-32 opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <p className="text-white/60 font-light text-sm leading-relaxed">
                      {service.content}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Steps to Join */}
        <section className="py-20 md:py-32 bg-black">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white text-center mb-16">
              Ready to join the <span className="text-[#B8860B]">BLR Nail Club</span> family?
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  step: "1",
                  title: "Explore Our Opportunity",
                  description: "Passionate about the beauty business? You're the perfect fit! Fill out our inquiry form to get started."
                },
                {
                  step: "2",
                  title: "Visit Our Salon",
                  description: "Get a firsthand look at our operations, service quality, and meet the BLR Nail Club team."
                },
                {
                  step: "3",
                  title: "Start Your Journey",
                  description: "Complete the franchise application and let's discuss your vision for your beauty business."
                }
              ].map((item, index) => (
                <div key={index} className="text-center p-8 border border-white/10 hover:border-[#B8860B]/30 transition-colors">
                  <div className="w-16 h-16 rounded-full bg-[#B8860B] text-black text-2xl font-light flex items-center justify-center mx-auto mb-6">
                    {item.step}
                  </div>
                  <h3 className="text-xl text-white font-light mb-4">{item.title}</h3>
                  <p className="text-white/60 font-light">{item.description}</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <button
                onClick={scrollToForm}
                className="bg-[#B8860B] text-black px-10 py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-[#D4AF37] transition-colors"
              >
                Start Your Application
              </button>
            </div>
          </div>
        </section>

        {/* Franchise Form Section */}
        <section ref={formRef} id="franchise-form" className="py-20 md:py-32 bg-[#0a0a0a]">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-6">
                Tell us about <span className="text-[#B8860B]">you</span>
              </h2>
              <p className="text-white/60 font-light text-lg">
                Fill in your details below. Our franchise team reviews every enquiry personally and responds within 24 hours.
              </p>
            </div>

            {/* Khoobventures Form Embed */}
            <div className="bg-black border border-white/10 rounded-lg overflow-hidden">
              <iframe 
                src="https://www.khoobventures.in/franchise"
                className="w-full border-0"
                style={{ height: '1200px' }}
                title="Franchise Enquiry Form - Khoob Ventures"
                loading="lazy"
              />
            </div>
            
            {/* Alternative CTA */}
            <div className="text-center mt-8">
              <p className="text-white/40 text-sm mb-4">
                Or apply directly on our franchise portal
              </p>
              <a
                href="https://www.khoobventures.in/franchise#enquiry"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-[#B8860B] text-[#B8860B] px-8 py-3 text-sm tracking-[0.15em] uppercase font-medium hover:bg-[#B8860B] hover:text-black transition-colors"
              >
                Open Full Application →
              </a>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 bg-[#B8860B]">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-2xl md:text-3xl text-black font-light mb-4">
              Questions? Call us at <a href="tel:+917204602211" className="underline">+91 72046 02211</a>
            </h2>
            <p className="text-black/70">
              Our franchise team is available Mon-Sat, 10 AM - 7 PM
            </p>
          </div>
        </section>
      </main>
    </>
  );
};

export default FranchisePage;
